
import React, { useEffect, useState } from 'react';
import { Facebook, Instagram, Youtube, Twitter, Heart, Send, Globe, Shield } from 'lucide-react';
import { getSocialLinks, fetchSocialLinksFromServer } from '../services/mockService';
import { SocialLinks, BrandingConfig } from '../types';

export const Footer: React.FC<{ branding: BrandingConfig }> = ({ branding }) => {
  const [links, setLinks] = useState<SocialLinks | null>(null);

  useEffect(() => {
    setLinks(getSocialLinks());
    fetchSocialLinksFromServer().then(s => setLinks(s)).catch(() => {});
  }, []);

  if (!links) return null;

  const hasAnyLink = Object.values(links).some(link => link !== '');

  return (
    <footer className="bg-templeRed dark:bg-stone-950 text-orange-100 dark:text-stone-400 py-2 px-4 mt-4 border-t border-saffron/50 dark:border-stone-800 transition-colors">
      <div className="max-w-7xl mx-auto flex flex-wrap justify-center items-center gap-x-4 gap-y-1 text-[10px]">
        
        {/* Brand */}
        <span className="font-hindi font-bold text-white dark:text-saffron">{branding.appName}</span>
        
        <span className="opacity-40">|</span>

        {/* Social Icons */}
        {hasAnyLink && (
          <div className="flex items-center gap-1.5">
            {links.facebook && (
              <a href={links.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-white dark:hover:text-saffron transition-all opacity-70 hover:opacity-100" title="Facebook">
                <Facebook size={12} />
              </a>
            )}
            {links.instagram && (
              <a href={links.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-white dark:hover:text-saffron transition-all opacity-70 hover:opacity-100" title="Instagram">
                <Instagram size={12} />
              </a>
            )}
            {links.youtube && (
              <a href={links.youtube} target="_blank" rel="noopener noreferrer" className="hover:text-white dark:hover:text-saffron transition-all opacity-70 hover:opacity-100" title="YouTube">
                <Youtube size={12} />
              </a>
            )}
            {links.twitter && (
              <a href={links.twitter} target="_blank" rel="noopener noreferrer" className="hover:text-white dark:hover:text-saffron transition-all opacity-70 hover:opacity-100" title="Twitter">
                <Twitter size={12} />
              </a>
            )}
            {links.whatsapp && (
              <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" className="hover:text-white dark:hover:text-saffron transition-all opacity-70 hover:opacity-100" title="WhatsApp">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.94 3.659 1.437 5.63 1.438h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            )}
            {links.telegram && (
              <a href={links.telegram} target="_blank" rel="noopener noreferrer" className="hover:text-white dark:hover:text-saffron transition-all opacity-70 hover:opacity-100" title="Telegram">
                <Send size={12} />
              </a>
            )}
          </div>
        )}

        <span className="opacity-40">|</span>

        <a 
          href="https://www.golokstore.com" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="flex items-center gap-1 bg-white/20 dark:bg-saffron/20 px-2 py-0.5 rounded-full text-yellow-200 dark:text-saffron hover:bg-white/30 dark:hover:bg-saffron/30 hover:text-white transition-all font-bold border border-yellow-300/30 dark:border-saffron/40" 
          data-testid="footer-website-link"
        >
          <Globe size={10} /> golokstore.com
        </a>

        <span className="opacity-40">|</span>

        <a 
          href="/privacy-policy.html" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="flex items-center gap-1 opacity-70 hover:opacity-100 hover:text-white dark:hover:text-saffron transition-all"
          data-testid="footer-privacy-link"
        >
          <Shield size={10} /> गोपनीयता नीति
        </a>

        <span className="opacity-40">|</span>

        {/* Copyright */}
        <span className="opacity-60 flex items-center gap-1">
          © {new Date().getFullYear()} <Heart size={8} className="fill-saffron text-saffron" /> Golok Prem Ras
        </span>
      </div>
    </footer>
  );
};
